import { Container, Sprite, type Texture } from "pixi.js";
import type { IntroScene, LocalizationService } from "@slot/game-shell";
import type { Log, PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import type { GameVisualAssets } from "../../assets/GameVisualAssets";
import { POTS, type PotKind } from "../../rules/symbols";
import { plaqueNode, type Plaque } from "../../scene/JackpotPlaquesView";
import { INTRO_LAYOUT, LOGO_LAYOUT } from "../../scene/layout";
import { multiplierLabel } from "../../scene/MultiplierBadgesView";
import { RespinMeterView } from "../../scene/RespinMeterView";
import { ShineSweeper } from "../../scene/ShineSweeper";
import { INTRO_TUNING } from "../../tuning/introTuning";
import { SHINE_TUNING } from "../../tuning/shineTuning";
import { TEXT, label } from "../textStyles";

/** One ember rising over the background. */
interface Ember {
  sprite: Sprite;
  x: number;
  y: number;
  speed: number;
  sway: number;
  age: number;
  life: number;
}

/** A pot as the intro draws it: its glow behind its urn, its name plate on its base. */
interface IntroPot {
  root: Container;
  glow: Sprite | null;
}

const between = (range: readonly [number, number]): number => range[0] + Math.random() * (range[1] - range[0]);
const lerp = (from: number, to: number, k: number): number => from + (to - from) * k;

/**
 * The title's intro (decision 77), after 3 Coin Volcanoes': the bonus's three boosters in a triangle under the logo,
 * each showing what it does on the game's own pieces — LIFE's respin meter running out and refilling, MULTI's ×2
 * dropping onto a MAJOR coin and going into it as the payout's multipliers do, GROW's ROYAL plaque (2000×) glinting —
 * the pots glowing in their colours, embers rising, "BONUS GAME FEATURES" under them. The shell's splash draws its
 * prompt and its badges over it. Fitted to the screen (never above its size), its motion on the UI's runner, looping
 * until the player's first gesture; a missing piece of art is left out.
 */
export class CoinHoldIntro implements IntroScene {
  public readonly node = new Container({ label: "CoinHoldIntro" });
  private readonly runner: PresentationTaskRunner;
  private readonly unsubscribe: () => void;
  private readonly pots: Record<PotKind, IntroPot>;
  private readonly meter: RespinMeterView;
  private readonly coin: Container;
  private readonly badge: Container;
  private readonly merged: Container;
  private readonly plaque: Plaque;
  private readonly sweeper = new ShineSweeper();
  private readonly embers: Ember[] = [];
  private running = false;

  public constructor(input: { assets: GameVisualAssets; logo: Texture | null; viewport: ViewportStore; runner: PresentationTaskRunner; localization: LocalizationService; log: Log }) {
    const { assets, runner } = input;
    const t = assets.textures;
    const L = INTRO_LAYOUT;
    this.runner = runner;
    const embers = new Container({ label: "Embers" });
    this.node.addChild(embers);
    if (input.logo) {
      const logo = new Sprite({ texture: input.logo, anchor: 0.5, label: "Logo" });
      logo.setSize(L.logo.width, (L.logo.width * LOGO_LAYOUT.height) / LOGO_LAYOUT.width);
      logo.position.set(0, L.logo.y);
      this.node.addChild(logo);
    }
    this.pots = Object.fromEntries(POTS.map((kind) => [kind, this.buildPot(kind, t.pots[kind], t.potLabels[kind], t.glow)])) as Record<PotKind, IntroPot>;
    // LIFE: the respin meter itself.
    this.meter = new RespinMeterView({ frame: t.meterFrame, slotOn: t.meterSlotOn, slotOff: t.meterSlotOff, runner, ambientRunner: runner });
    this.meter.position.set(L.meter.x, L.meter.y);
    this.meter.scale.set(L.meter.scale);
    this.meter.setShown(true);
    void this.meter.setCount(3, 0);
    // MULTI: a MAJOR coin, the ×2 that drops on it, and its ×2 once it has gone in.
    this.coin = new Container({ label: "MajorCoin" });
    this.coin.position.set(L.coin.x, L.coin.y);
    const coinArt = new Sprite({ texture: assets.symbols.jackpot.major, anchor: 0.5, label: "Art" });
    coinArt.setSize(L.coin.size, L.coin.size);
    this.merged = new Container({ label: "Merged" });
    this.merged.addChild(multiplierLabel("x2"));
    this.merged.position.set(0, L.badge.merged.y);
    this.merged.scale.set(L.badge.merged.scale);
    this.merged.alpha = 0;
    this.coin.addChild(coinArt, this.merged);
    this.badge = new Container({ label: "Badge" });
    this.badge.addChild(multiplierLabel("x2"));
    this.badge.alpha = 0;
    // GROW: the ROYAL plaque, its 2000×.
    this.plaque = plaqueNode("royal", t.plaques.royal);
    this.plaque.amount.text = "2000x";
    this.plaque.root.position.set(L.plaque.x, L.plaque.y);
    this.plaque.root.scale.set(L.plaque.scale);
    const heading = label({ localization: input.localization, key: "intro.features", fallback: "BONUS GAME FEATURES", style: TEXT.heading(), fontSize: L.heading.fontSize, y: L.heading.y, box: L.heading.box, label: "Heading" });
    this.node.addChild(this.meter, this.coin, this.badge, this.plaque.root, heading);
    if (t.sparkle) {
      for (let i = 0; i < INTRO_TUNING.embers.count; i++) {
        const sprite = new Sprite({ texture: t.sparkle, anchor: 0.5, tint: INTRO_TUNING.embers.tint, label: "Ember" });
        sprite.blendMode = "add";
        embers.addChild(sprite);
        const ember: Ember = { sprite, x: 0, y: 0, speed: 0, sway: 0, age: 0, life: 1 };
        this.spawn(ember, Math.random());
        this.embers.push(ember);
      }
    }
    this.unsubscribe = input.viewport.subscribe((viewport) => {
      const { width, height } = viewport.design;
      const scale = Math.min(1, width / L.box.width, height / L.box.height);
      this.node.scale.set(scale, scale);
    });
  }

  public start(): void {
    if (this.running) return;
    this.running = true;
    void this.loop(() => this.life());
    void this.loop(() => this.multi());
    void this.loop(() => this.grow());
    this.ambient();
  }

  public dispose(): void {
    this.running = false;
    this.unsubscribe();
    this.sweeper.stop(this.plaque.root);
    this.node.destroy({ children: true });
  }

  /** A pot of `kind`: its glow behind its urn, its name plate on its base, at its spot. */
  private buildPot(kind: PotKind, urn: Texture | null, name: Texture | null, glowTexture: Texture | null): IntroPot {
    const L = INTRO_LAYOUT;
    const root = new Container({ label: `Pot:${kind}` });
    root.position.set(L.pots[kind].x, L.pots[kind].y);
    const height = urn ? (L.potWidth * urn.height) / urn.width : L.potWidth;
    let glow: Sprite | null = null;
    if (glowTexture) {
      glow = new Sprite({ texture: glowTexture, anchor: 0.5, tint: INTRO_TUNING.glow.tint[kind], label: "Glow" });
      glow.blendMode = "add";
      glow.setSize(L.potWidth * L.glow, L.potWidth * L.glow);
      glow.alpha = INTRO_TUNING.glow.alpha[0];
      root.addChild(glow);
    }
    if (urn) {
      const art = new Sprite({ texture: urn, anchor: 0.5, label: "Urn" });
      art.setSize(L.potWidth, height);
      root.addChild(art);
    }
    if (name) {
      const plate = new Sprite({ texture: name, anchor: 0.5, label: "Name" });
      const width = L.potWidth * L.potLabel.width;
      plate.setSize(width, (width * name.height) / name.width);
      plate.position.set(0, height * L.potLabel.y);
      root.addChild(plate);
    }
    this.node.addChild(root);
    return { root, glow };
  }

  /** `body` again and again while the intro shows. */
  private async loop(body: () => Promise<void>): Promise<void> {
    while (this.running && !this.node.destroyed) await body();
  }

  /** LIFE's meter: full, then a slot at a time down to none, then all three back as LIFE refills it. */
  private async life(): Promise<void> {
    const tune = INTRO_TUNING.life;
    await this.runner.sleep(tune.fullSeconds);
    for (const count of [2, 1, 0]) {
      if (!this.running) return;
      void this.meter.setCount(count, 0, tune.drainSeconds);
      await this.runner.sleep(tune.drainEverySeconds);
    }
    await this.runner.sleep(tune.emptySeconds);
    if (!this.running) return;
    this.flare("life");
    await this.meter.setCount(3, tune.refillPopSeconds);
  }

  /** MULTI's ×2: it falls onto the coin's corner, rests, goes into the coin, and the coin shows ×2 for a while. */
  private async multi(): Promise<void> {
    const tune = INTRO_TUNING.multi;
    const { coin, badge: spot } = INTRO_LAYOUT;
    const badge = this.badge;
    const corner = { x: coin.x + spot.corner.x, y: coin.y + spot.corner.y };
    const tween = (seconds: number, step: (k: number) => void, easing: "linear" | "quadIn" | "quadInOut" = "linear"): Promise<void> =>
      this.runner.tweenProgress(seconds, (k) => {
        if (!badge.destroyed) step(k);
      }, undefined, { easing });
    badge.scale.set(spot.scale);
    await tween(tune.fallSeconds, (k) => {
      badge.position.set(corner.x, lerp(coin.y + spot.from, corner.y, k));
      badge.alpha = Math.min(1, k * 3);
    }, "quadIn");
    await tween(tune.landPopSeconds, (k) => badge.scale.set(spot.scale * (1 + 0.25 * Math.sin(Math.PI * k))));
    await this.runner.sleep(tune.restSeconds);
    // Into the coin, as the payout's multipliers go: onto its centre, growing, then swelling out.
    await tween(tune.mergeMoveSeconds, (k) => {
      badge.position.set(lerp(corner.x, coin.x, k), lerp(corner.y, coin.y, k));
      badge.scale.set(spot.scale * (1 + 0.4 * k));
    }, "quadInOut");
    await tween(tune.mergePopSeconds, (k) => {
      badge.scale.set(spot.scale * (1.4 + 0.3 * Math.sin(Math.PI * Math.min(1, k * 1.6))));
      badge.alpha = k < 0.55 ? 1 : 1 - (k - 0.55) / 0.45;
    });
    // Taken down meanwhile (the player's tap): nothing more to touch.
    if (!this.running) return;
    badge.alpha = 0;
    this.merged.alpha = 1;
    await tween(tune.swellSeconds, (k) => this.coin.scale.set(1 + 0.12 * Math.sin(Math.PI * k)));
    await this.runner.sleep(tune.showSeconds);
    await tween(tune.fadeSeconds, (k) => (this.merged.alpha = 1 - k));
    await this.runner.sleep(tune.gapSeconds);
  }

  /** GROW's ROYAL plaque glints, and its pot flares. */
  private async grow(): Promise<void> {
    await this.runner.sleep(INTRO_TUNING.grow.glintEverySeconds);
    if (!this.running || this.node.destroyed) return;
    this.flare("grow");
    await this.sweeper.sweep(this.plaque.root, SHINE_TUNING.event.glint, this.runner);
  }

  /** A pot's glow flares up as its booster shows what it does (the pulse takes it back). */
  private flare(kind: PotKind): void {
    const glow = this.pots[kind].glow;
    if (glow && !glow.destroyed) glow.alpha = 1;
  }

  /** The embers rising and the pots' glows pulsing, every frame while the intro shows. */
  private ambient(): void {
    const { glow: pulse } = INTRO_TUNING;
    const [low, high] = pulse.alpha;
    let last = 0;
    void this.runner.tickInScope(
      "",
      (elapsed) => {
        if (!this.running) return;
        const delta = elapsed - last;
        last = elapsed;
        POTS.forEach((kind, i) => {
          const glow = this.pots[kind].glow;
          if (!glow) return;
          const beat = low + (high - low) * (0.5 + 0.5 * Math.sin((2 * Math.PI * (elapsed / pulse.periodSeconds + i / 3))));
          // A flare eases back down to the pulse.
          glow.alpha = Math.max(beat, glow.alpha - delta * 1.5);
        });
        for (const ember of this.embers) {
          ember.age += delta;
          if (ember.age >= ember.life) this.spawn(ember, 0);
          ember.y -= ember.speed * delta;
          const k = ember.age / ember.life;
          ember.sprite.position.set(ember.x + Math.sin(ember.age * 2 + ember.sway) * 18, ember.y);
          ember.sprite.alpha = Math.min(1, k * 4, (1 - k) * 3);
        }
      },
      () => !this.running || this.node.destroyed,
    );
  }

  /** An ember set out again from under the box, `age` a share of its life already spent (spread at the start). */
  private spawn(ember: Ember, age: number): void {
    const { box } = INTRO_LAYOUT;
    const { speed, lifeSeconds, size } = INTRO_TUNING.embers;
    ember.life = between(lifeSeconds);
    ember.age = ember.life * age;
    ember.speed = between(speed);
    ember.sway = Math.random() * Math.PI * 2;
    ember.x = (Math.random() - 0.5) * box.width;
    ember.y = box.height / 2 - ember.speed * ember.age;
    const side = between(size);
    ember.sprite.setSize(side, side);
  }
}
